import { useMutation } from '@apollo/client';
import { selectUserRbacPermissions } from 'core/userContext';
import isEmpty from 'lodash/isEmpty';
import { ExitRules } from 'modules/journey/components/ExitRules';
import { transformExitRulesForMutation } from 'modules/journey/components/ExitRules/ExitRules.utils';
import { saveExitRulesMutation } from 'modules/journey/components/ExitRules/api/exitRulesMutations';
import { useInitialClonedRulesErrorCheck } from 'modules/journey/components/RuleBuilder/hooks';
import {
  RuleBuilderProvider,
  useRuleBuilder,
} from 'modules/journey/components/RuleBuilder/hooks/useRuleBuilder';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import {
  CLONE_ERROR_BANNER_ID,
  CloneErrorBanner,
} from 'modules/workflowNodeEditors/components/CloneErrorBanner';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { ErrorBanner, InfoBanner, WarningBanner } from 'ui/banner';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { spacing } from 'ui/theme';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { useExitRuleContext } from 'modules/workflows/context/ExitRuleContext';
import { useEffect } from 'react';
import { SettingsTab } from 'modules/journey/components/SettingsTakeover/SettingsTakeover.constants';
import { EXIT_RULES_MODAL } from '../../../constants';
import {
  closeModal,
  closeTakeover,
  getOpenedNodeId,
  getWorkflow,
  getWorkflowType,
  openTakeover,
} from '../../../ducks';
import { TakeoverType } from '../../takeovers';

export const SAVE_EXIT_RULES_BUTTON_TEST_ID = 'save-exit-rules-button';
export const DRAFT_EXIT_RULES_BANNER = 'draft-exit-rules-banner';
export const PUBLISHED_EXIT_RULES_BANNER = 'published-exit-rules-banner';

export type ExitRulesModalProps = {
  /** Whether the modal is open */
  isOpen: boolean;
  getExitRuleCount: () => void;
};

const StyledModalBanner = styled.div`
  margin: 0 ${spacing.plusHalf} ${spacing.base};
  > div {
    margin-bottom: 0;
  }
`;

const ModalBanner = ({
  isDraft,
  hasValidationErrors,
}: {
  isDraft: boolean;
  hasValidationErrors: boolean;
}) => {
  if (hasValidationErrors)
    return (
      <StyledModalBanner>
        <ErrorBanner data-test="exit-rules-error-banner">
          Please fix the highlighted errors.
        </ErrorBanner>
      </StyledModalBanner>
    );
  const banner = isDraft ? (
    <InfoBanner data-test={DRAFT_EXIT_RULES_BANNER}>
      This set of exit rules is saved to this draft and will only be applied to the live journey
      once the draft is published.
    </InfoBanner>
  ) : (
    <WarningBanner data-test={PUBLISHED_EXIT_RULES_BANNER}>
      Any changes to these exit rules will affect the live, published journey immediately.
    </WarningBanner>
  );

  return <StyledModalBanner>{banner}</StyledModalBanner>;
};

export const ExitRulesModalView: React.FC<React.PropsWithChildren<ExitRulesModalProps>> = ({
  isOpen,
  getExitRuleCount,
}) => {
  const workflow = useSelector(getWorkflow);
  const permissions = useSelector(selectUserRbacPermissions);
  const hasDraftMessaging = permissions.includes(PROJECT_PERMISSIONS.DraftMessaging);
  const openedNodeId = useSelector(getOpenedNodeId);
  const { state, isLoading, checkForValidationErrors, validationErrors, hasCloningErrors } =
    useRuleBuilder();

  const { rules } = state;
  const { id: workflowId, workflowType } = workflow;
  const { isDraft } = useSelector(getWorkflowType);

  const dispatch = useDispatch();

  const handleCloseModal = () => dispatch(closeModal(EXIT_RULES_MODAL));

  const [saveExitRules, { loading: isSavingExitRules }] = useMutation(saveExitRulesMutation, {
    onCompleted: () => {
      dispatch(addPositiveToast('Exit rules saved!'));
      handleCloseModal();
    },
    onError: () => {
      dispatch(addNegativeToastWithErrorId('Exit rules could not be saved.', 'saveExitRules'));
    },
  });

  const handleSaveExitRules = async () => {
    const hasErrors = checkForValidationErrors();
    if (!hasErrors) {
      const transformedRules = transformExitRulesForMutation(rules);
      await saveExitRules({
        variables: {
          workflowId,
          workflowType,
          rules: transformedRules,
        },
      });
      getExitRuleCount();
      handleCloseModal();
    }
  };

  const openConversionTab = () => {
    dispatch(
      openTakeover(TakeoverType.Settings, {
        focusedTab: SettingsTab.About,
        hasDraftMessaging,
        isOverlay: !openedNodeId,
        onClose: () => dispatch(closeTakeover(TakeoverType.Settings)),
      }),
    );
    dispatch(closeModal(EXIT_RULES_MODAL));
  };

  return (
    <Modal
      id={EXIT_RULES_MODAL}
      data-test={`modal-${EXIT_RULES_MODAL}`}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.LARGE}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader autoFocus={false}>Exit rules</ModalHeader>
      {hasCloningErrors && (
        <StyledModalBanner data-test={CLONE_ERROR_BANNER_ID}>
          <CloneErrorBanner />
        </StyledModalBanner>
      )}
      {!hasCloningErrors && (
        <ModalBanner isDraft={isDraft} hasValidationErrors={!isEmpty(validationErrors)} />
      )}
      <ModalBody>
        {!isLoading ? <ExitRules goToConversionTab={openConversionTab} /> : <div>Loading</div>}
      </ModalBody>
      <ModalFooter>
        <Button
          data-test={`${SAVE_EXIT_RULES_BUTTON_TEST_ID}${
            isSavingExitRules || !hasDraftMessaging ? '-disabled' : ''
          }`}
          onClick={handleSaveExitRules}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.PRIMARY}
          isDisabled={isSavingExitRules || !hasDraftMessaging || !isEmpty(validationErrors)}
        >
          Update
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export const ExitRulesModal: React.FC<React.PropsWithChildren<ExitRulesModalProps>> = ({
  isOpen,
}) => {
  const { exitRules, loadingExitRules, getExitRuleCriteria, getExitRules } = useExitRuleContext();

  useEffect(() => {
    getExitRules();
  }, []);

  const isClonedWithErrors = useInitialClonedRulesErrorCheck(exitRules);

  return (
    <RuleBuilderProvider
      rules={exitRules}
      isClonedWithErrors={isClonedWithErrors}
      isLoading={loadingExitRules}
    >
      <ExitRulesModalView isOpen={isOpen} getExitRuleCount={getExitRuleCriteria} />
    </RuleBuilderProvider>
  );
};
